import React, { useState, useEffect, useRef } from 'react';
import { cvApi } from '../services/api';
import './UserCvSection.css';

function UserCvSection({ userId }) {
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB

  useEffect(() => {
    fetchUserCvs();
  }, [userId]);

  const fetchUserCvs = async () => {
    try {
      setLoading(true);
      setError(null);
      // Try to fetch user-specific CVs (this endpoint may not exist yet)
      try {
        const data = await cvApi.getUserCvs(userId);
        setCvs(data);
      } catch (err) {
        // If the endpoint doesn't exist, just show empty state
        setCvs([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching CVs:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateFile = (file) => {
    if (!file) return 'No file selected';
    if (!ALLOWED_TYPES.includes(file.type)) {
      return 'Only PDF, DOC, and DOCX files are allowed';
    }
    if (file.size > MAX_SIZE) {
      return 'File size must be less than 10MB';
    }
    return null;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const error = validateFile(file);
      if (error) {
        alert(error);
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      alert('Please select a file first');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccessMessage(null);

      // Pass userId to upload CV for the specific user (not the logged-in user)
      const result = await cvApi.upload(selectedFile, userId);
      setSuccessMessage(`CV "${result.fileName}" uploaded successfully!`);

      // Clear file selection
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh CV list
      await fetchUserCvs();

      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message);
      console.error('Error uploading CV:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (cv) => {
    try {
      const blob = await cvApi.download(cv.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = cv.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err.message);
      console.error('Error downloading CV:', err);
    }
  };

  const handleDelete = async (cvId) => {
    if (!window.confirm('Are you sure you want to delete this CV?')) {
      return;
    }

    try {
      await cvApi.delete(cvId);
      setSuccessMessage('CV deleted successfully');
      await fetchUserCvs();
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (err) {
      setError(err.message);
      console.error('Error deleting CV:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    switch (extension) {
      case 'pdf':
        return '📕';
      case 'doc':
      case 'docx':
        return '📘';
      default:
        return '📄';
    }
  };

  return (
    <div className="user-cv-section">
      <div className="section-header">
        <h3>📄 Curriculum Vitae</h3>
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="alert-close">&times;</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="alert-close">&times;</button>
        </div>
      )}

      <div className="cv-upload-area">
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          disabled={uploading}
        />

        <button
          onClick={() => fileInputRef.current?.click()}
          className="btn-select-file"
          disabled={uploading}
        >
          📎 {selectedFile ? selectedFile.name : 'Choose CV File'}
        </button>

        {selectedFile && (
          <button
            onClick={handleUpload}
            disabled={uploading}
            className="btn-upload"
          >
            {uploading ? '⏳ Uploading...' : '⬆️ Upload CV'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading-text">Loading CVs...</div>
      ) : cvs.length === 0 ? (
        <div className="empty-state">
          <p>No CVs uploaded for this user</p>
        </div>
      ) : (
        <div className="cv-list-compact">
          {cvs.map((cv) => (
            <div key={cv.id} className="cv-item">
              <div className="cv-item-left">
                <span className="file-icon">{getFileIcon(cv.fileName)}</span>
                <div className="cv-details">
                  <div className="cv-name">{cv.fileName}</div>
                  <div className="cv-meta">
                    {formatFileSize(cv.fileSizeBytes)} • {formatDate(cv.uploadedAt)}
                  </div>
                </div>
              </div>
              <div className="cv-item-actions">
                <button
                  onClick={() => handleDownload(cv)}
                  className="btn-icon-small"
                  title="Download"
                >
                  ⬇️
                </button>
                <button
                  onClick={() => handleDelete(cv.id)}
                  className="btn-icon-small btn-danger-small"
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default UserCvSection;
